import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PaymentsIcon from "@mui/icons-material/Payments";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useCompany } from "../../components/context/CompanyContext";
import IndicadoresNav from "../../components/IndicadoresNav";
import Loading from "../../components/loading/loading";
import { titleSetRef } from "../../redux/titleRedux";
import { userRequest } from "../../requestMethods";
import { formatPricing } from "../../util/helper";
import { effectiveStories } from "../../util/storeScope";
import "../../App.css";

const BRAND = "#EE8253";

const METHOD_CONFIG = {
  total:      { label: "Total Recebido",    gradient: ["#8B5CF6", "#6D28D9"], icon: PaymentsIcon    },
  creditCard: { label: "Cartão de Crédito", gradient: ["#3B82F6", "#1D4ED8"], icon: CreditCardIcon  },
  pix:        { label: "Pix",               gradient: ["#10B981", "#059669"], icon: QrCodeIcon      },
  mooda:      { label: "Mooda",             gradient: ["#EE8253", "#E8502A"], icon: PhoneAndroidIcon },
  dinheiro:   { label: "Dinheiro",          gradient: ["#F59E0B", "#D97706"], icon: AttachMoneyIcon  },
  outros:     { label: "Outros",            gradient: ["#6B7280", "#374151"], icon: MoreHorizIcon   },
};

const PIE_COLORS = ["#EE8253", "#3B82F6", "#10B981", "#F59E0B", "#6B7280"];

function PaymentCard({ methodKey, value, onClick }) {
  const [hover, setHover] = useState(false);
  const cfg = METHOD_CONFIG[methodKey];
  const Icon = cfg.icon;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: `linear-gradient(135deg, ${cfg.gradient[0]}, ${cfg.gradient[1]})`,
        borderRadius: 16,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.18s, box-shadow 0.18s",
        transform: hover ? "translateY(-3px)" : "none",
        boxShadow: hover ? "0 10px 28px rgba(0,0,0,0.20)" : "0 3px 10px rgba(0,0,0,0.10)",
        position: "relative",
      }}
    >
      <div style={{ position: "absolute", right: -24, top: -24, width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.10)", pointerEvents: "none" }} />
      <div style={{ padding: "16px 18px 12px", position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.80)", textTransform: "uppercase", letterSpacing: "0.09em", fontFamily: "Manrope" }}>{cfg.label}</span>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: "rgba(255,255,255,0.20)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon style={{ fontSize: 18, color: "#fff" }} />
          </div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", fontFamily: "Manrope", letterSpacing: "-0.02em", lineHeight: 1.1 }}>{formatPricing(value || 0)}</div>
      </div>
      {onClick && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", padding: "8px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(0,0,0,0.10)" }}>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontFamily: "Manrope", fontWeight: 600 }}>Ver pedidos</span>
          <span style={{ fontSize: 14, color: "rgba(255,255,255,0.65)" }}>›</span>
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, sub, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "18px 18px 14px" }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope", marginTop: 2 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}



export default function IndicadoresPagamentos() {
  const [payments, setPayments] = useState(null);
  const [consolidate, setConsolidate] = useState([]);
  const [dataIni, setDateIni] = useState(() => dayjs().add(-7, "day").format("YYYY-MM-DD"));
  const [dataFim, setDateFim] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [readonly, setReadonly] = useState("7");
  const [isLoading, setIsLoading] = useState(true);
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [storeIds, setStoreIds] = useState([]);

  const currentUser = useSelector((state) => state.user.currentUser);
  const { companyId, slug } = useCompany();
  const nav = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(titleSetRef({ title: "Indicadores Pgto" }));
    if (!currentUser) nav("../login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyId > 0 && currentUser) getStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    setStoreIds(selected.map((s) => s.value));
  }, [selected]);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      setIsLoading(true);
      carregarTudo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dataIni, dataFim, storeIds]);

  const buildObj = () => ({
    paymentStart: dataIni,
    paymentEnd: dataFim,
    stories: effectiveStories(currentUser, storeIds),
  });

  const getStories = async () => {
    try {
      const res = await userRequest.get(`${companyId}/store/stories`);
      const list = res.data.map((item) => ({ label: item.store, value: item.storeId }));
      setStories(list);
      if (currentUser?.position === "Gerente") {
        const userStore = list.find((s) => s.value === currentUser.storeId);
        if (userStore) setSelected([userStore]);
      }
    } catch {}
  };

  async function carregarTudo() {
    try {
      await Promise.all([getPayments(), getConsolidate()]);
    } finally {
      setIsLoading(false);
    }
  }

  const getPayments = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportPayments/amountPayments`, buildObj());
      setPayments(res.data);
    } catch {}
  };

  const getConsolidate = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportPayments/consolidatePayments`, buildObj());
      setConsolidate(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const PAYMENT_TYPE_IDS = { mooda: 1, pix: 2, creditCard: 3, dinheiro: 4 };

  const storeQSIds = effectiveStories(currentUser, storeIds);
  const storeQS = storeQSIds.length > 0 ? `&storeId=${storeQSIds.join(",")}` : "";

  const navToOrders = (methodKey) => {
    const base = `/${slug}/orders?start=${dataIni}&end=${dataFim}${storeQS}&from=indicadorespagamentos`;
    const typeId = PAYMENT_TYPE_IDS[methodKey];
    nav(typeId ? `${base}&paymentTypeIds=${typeId}` : base);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setReadonly(v);
    switch (v) {
      case "0":  setDateIni(dayjs().format("YYYY-MM-DD"));              setDateFim(dayjs().format("YYYY-MM-DD"));              break;
      case "7":  setDateIni(dayjs().add(-7,  "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "15": setDateIni(dayjs().add(-15, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "30": setDateIni(dayjs().add(-30, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      default: break;
    }
  };

  const periodLabel = readonly === "F" ? "filtro personalizado" : readonly === "0" ? "hoje" : `últimos ${readonly} dias`;

  const consolidateChart = consolidate.map((row) => ({
    date:     dayjs(row.paymentAt).format("DD/MM"),
    Mooda:    row.paymentTypeId1    || 0,
    Cartão:   row.paymentTypeId3    || 0,
    Pix:      row.paymentTypeId2    || 0,
    Dinheiro: row.paymentTypeId4    || 0,
    Outros:   row.paymentTypeIdOutros || 0,
  }));

  const brlCompact = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", notation: "compact", maximumFractionDigits: 1 }).format(v);

  const pieData = payments
    ? [
        { name: "Mooda",    value: payments.mooda      || 0 },
        { name: "Cartão",   value: payments.creditCard  || 0 },
        { name: "Pix",      value: payments.pix         || 0 },
        { name: "Dinheiro", value: payments.dinheiro    || 0 },
        { name: "Outros",   value: payments.outros      || 0 },
      ]
    : [];

  return (
    <div style={{ padding: "16px 16px 48px 4px", fontFamily: "Manrope", background: "#F9FAFB", minHeight: "100vh" }}>

      <IndicadoresNav />

      {/* Header + filtros */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Indicadores Pgto</span>
          <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>{periodLabel}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>

          {/* Pills Lojas */}
          {stories.length > 0 && (() => {
            const isGerente = currentUser?.position === "Gerente";
            return (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Lojas</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
                  <label style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${selected.length === 0 ? BRAND : "#E5E7EB"}`, background: selected.length === 0 ? BRAND : "transparent", color: selected.length === 0 ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600 }}
                    onClick={() => !isGerente && setSelected([])}>Todas</label>
                  {stories.map((story) => {
                    const isActive = selected.some((s) => s.value === story.value);
                    return (
                      <label key={story.value}
                        style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${isActive ? BRAND : "#E5E7EB"}`, background: isActive ? BRAND : "transparent", color: isActive ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600, opacity: isGerente ? 0.7 : 1 }}
                        onClick={() => { if (isGerente) return; setSelected(isActive ? selected.filter((s) => s.value !== story.value) : [...selected, story]); }}>
                        {story.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Pills Período */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Período</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
              {[{ v: "30", l: "30 dias" }, { v: "15", l: "15 dias" }, { v: "7", l: "7 dias" }, { v: "0", l: "Hoje" }, { v: "F", l: "Filtro" }].map(({ v, l }) => (
                <label key={v} style={{ cursor: "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${readonly === v ? BRAND : "#E5E7EB"}`, background: readonly === v ? BRAND : "transparent", color: readonly === v ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600 }}>
                  <input type="radio" value={v} checked={readonly === v} onChange={handleChange} style={{ display: "none" }} />
                  {l}
                </label>
              ))}
              {readonly === "F" && (
                <>
                  <div style={{ width: 1, background: "#E5E7EB", margin: "2px 2px" }} />
                  <input type="date" value={dataIni} onChange={(e) => setDateIni(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                  <span style={{ color: "#9CA3AF", fontSize: 12 }}>—</span>
                  <input type="date" value={dataFim} onChange={(e) => setDateFim(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* KPI cards — 3 colunas × 2 linhas */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
            <PaymentCard methodKey="total"      value={payments?.total}      onClick={() => navToOrders("total")}      />
            <PaymentCard methodKey="creditCard" value={payments?.creditCard} onClick={() => navToOrders("creditCard")} />
            <PaymentCard methodKey="pix"        value={payments?.pix}        onClick={() => navToOrders("pix")}        />
            <PaymentCard methodKey="mooda"      value={payments?.mooda}      onClick={() => navToOrders("mooda")}      />
            <PaymentCard methodKey="dinheiro"   value={payments?.dinheiro}   onClick={() => navToOrders("dinheiro")}   />
            <PaymentCard methodKey="outros"     value={payments?.outros}     onClick={() => navToOrders("outros")}     />
          </div>

          {/* Mix + Consolidado por dia */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 }}>

            {/* Gráfico de mix */}
            <ChartCard title="Mix de Pagamentos" sub={periodLabel}>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatPricing(v)} />
                  <Legend formatter={(v) => <span style={{ fontSize: 11, fontFamily: "Manrope" }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Consolidado por dia */}
            <ChartCard title="Recebimentos por Dia" sub={`data de pagamento · ${periodLabel}`}>
              {consolidateChart.length === 0 ? (
                <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF", fontSize: 13, fontFamily: "Manrope" }}>
                  Sem dados no período
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={consolidateChart} margin={{ top: 4, right: 8, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                    <YAxis tick={{ fontSize: 11, fontFamily: "Manrope" }} tickFormatter={brlCompact} width={62} />
                    <Tooltip
                      formatter={(v, name) => [formatPricing(v), name]}
                      contentStyle={{ fontFamily: "Manrope", fontSize: 12, borderRadius: 8, border: "1px solid #E5E7EB" }}
                    />
                    <Legend formatter={(v) => <span style={{ fontSize: 11, fontFamily: "Manrope" }}>{v}</span>} />
                    <Bar dataKey="Mooda"    stackId="a" fill="#EE8253" barSize={22} />
                    <Bar dataKey="Cartão"   stackId="a" fill="#3B82F6" barSize={22} />
                    <Bar dataKey="Pix"      stackId="a" fill="#10B981" barSize={22} />
                    <Bar dataKey="Dinheiro" stackId="a" fill="#F59E0B" barSize={22} />
                    <Bar dataKey="Outros"   stackId="a" fill="#6B7280" barSize={22} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>


        </div>
      )}
    </div>
  );
}
